import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Stack } from '@mui/material';
import HikingIcon from '@mui/icons-material/Hiking';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import SpaIcon from '@mui/icons-material/Spa';
import NightlightIcon from '@mui/icons-material/Nightlight';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import FlightIcon from '@mui/icons-material/Flight';
import { Icon } from "@iconify/react";
import Stagger from './Stagger';
import Reveal from './Reveal';

const activities = [
  { icon: <HikingIcon color="success" />, label: 'Senderismo y Trekking' },
  { icon: <Icon icon="mdi:horse-human" width="24" height="24" style={{color: '#2e7d32'}}	 />, label: 'Cabalgatas' },
  { icon: <DirectionsBikeIcon color="success" />, label: 'Ciclismo' },
  { icon: <DirectionsRunIcon color="success" />, label: 'Escalada' },
  { icon: <SpaIcon color="success" />, label: 'Turismo de bienestar' },
  { icon: <NightlightIcon color="success" />, label: 'Astroturismo' },
];

const routes = [
  {
    icon: <DirectionsCarIcon color="primary" />,
    title: 'Desde Córdoba Capital',
    description: 'A solo 50 km, La Granja se encuentra en Sierras Chicas, con fácil acceso por ruta E-53.',
    distance: '50 km'
  },
  {
    icon: <DirectionsBusIcon color="primary" />,
    title: 'Desde el Valle de Punilla',
    description: 'Podes llegar por la Ruta E-98, más conocida como Camino El Cuadrado. Un recorrido pintoresco por las Sierras que te conecta con la E-53.',
    distance: 'Ruta E-98'
  },
  {
    icon: <FlightIcon color="primary" />,
    title: 'Desde otras ciudades',
    description: 'Llegá al Aeropuerto de Córdoba y luego tomá un transporte hacia La Granja. Puede ser un transporte privado, público, o el alquiler de un vehículo.',
    distance: 'Aeropuerto'
  }
];

export default function Discover() {
  return (
    <Box id="descubri" sx={{ py: 8, background: '#f8fafc' }}>
      <Box maxWidth={1200} mx="auto" px={2}>
        <Stagger baseDelay={0} step={120} variants={['slide-left', 'slide-right']} distance={22}>
        {/* Contenedor principal: Texto + Imagen */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'flex-start', mb: 2 }}>
          {/* Columna izquierda - Texto */}
          <Box sx={{ flex: 1, pr: { md: 2 } }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              Descubrí La Granja
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 4, color: 'text.secondary' }}>
              Descubre el encanto natural de La Granja
            </Typography>
            <Typography variant="h6" sx={{ color: 'primary.main', mb: 3, fontWeight: 700, fontSize: '1.1rem' }}>
              A 48 KM DE LA CAPITAL
            </Typography>
            <Typography sx={{ mb: 3, lineHeight: 1.6, color: 'text.primary', textAlign: 'justify' }}>
              Un destino turístico y cultural, ubicado a solo 48 km del Aeropuerto de la provincia de Córdoba, por ruta E-53. Esta región es un tesoro escondido, conformada por 5 pueblos: Las Vertientes de La Granja, Villa Ani Mi, Los Molles, Ascochinga y La Granja.
            </Typography>
            <Typography sx={{ mb: 3, lineHeight: 1.6, color: 'text.primary', textAlign: 'justify' }}>
              La Granja es un paraíso natural surcado por los ríos Tiu Mayu y Carapé, con zonas protegidas que la convierten en un refugio para aquellos que buscan disfrutar de la naturaleza, el turismo, la cultura y el descanso.
            </Typography>
            <Typography sx={{ mb: 3, lineHeight: 1.6, color: 'text.primary', textAlign: 'justify' }}>
              Contamos con dos Reservas Naturales: la Reserva Tiu Mayu en La Granja y la Rserva Natural de la Defensa de Ascochinga, espacios únicos para la conservación y la aventura.
            </Typography>
            <Typography sx={{ mb: 3, lineHeight: 1.6, color: 'text.primary', textAlign: 'justify' }}>
              Su ubicación estratégicas la convierte en un punto de partida perfecto para explorar las bellezas naturales de la región.
            </Typography>
          </Box>
          
          {/* Columna derecha - Imagen y Card */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* Imagen */}
            <Box sx={{ 
              borderRadius: 3, 
              overflow: 'hidden',
              boxShadow: 3,
              display: { xs: 'none', md: 'block' }, // Ocultar en móvil, mostrar en desktop
              transform: 'translate3d(0,0,0)',
              transition: 'transform 260ms ease, box-shadow 260ms ease',
              '&:hover': {
                transform: 'translate3d(0,-6px,0)',
                boxShadow: '0 18px 46px rgba(0,0,0,0.18)',
              },
              '&:hover img': { transform: 'scale(1.04)' },
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
                '&:hover': { transform: 'none' },
                '&:hover img': { transform: 'none' },
              },
            }}>
              <img
                src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-1.2.1&auto=format&fit=crop&w=1050&q=80"
                alt="Paisaje de La Granja"
                style={{ 
                  width: '100%', 
                  height: 350, 
                  objectFit: 'cover',
                  display: 'block',
                  transform: 'scale(1)',
                  transition: 'transform 800ms cubic-bezier(0.2, 0.8, 0.2, 1)',
                }}
              />
            </Box>
            
            {/* Card de Turismo Activo */}
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: 3,
              border: '1px solid #e0e0e0',
              transform: 'translate3d(0,0,0)',
              transition: 'transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease',
              '&:hover': {
                transform: 'translate3d(0,-6px,0)',
                boxShadow: '0 16px 40px rgba(0,0,0,0.16)',
                borderColor: 'rgba(240,150,31,0.35)',
              },
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
                '&:hover': { transform: 'none' },
              },
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'primary.main', fontSize: '1.1rem' }}>
                  Turismo Activo y de Aventura
                </Typography>
                <Typography sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.5 }}>
                La Granja es el destino ideal para los amantes de la acción y la naturaleza:
                </Typography>
                 <Grid container spacing={2}>
                   {activities.map((act, idx) => (
                     <Grid item xs={12} sm={6} md={4} key={idx}>
                      <Reveal
                        delay={140 + idx * 70}
                        variant={idx % 2 === 0 ? 'slide-left' : 'slide-right'}
                        distance={18}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {act.icon}
                          </Box>
                          <Typography sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {act.label}
                          </Typography>
                        </Stack>
                      </Reveal>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Box>
        </Box>

         {/* Sección Cómo llegar - Card única */}
         <Box sx={{ mb: 6 }}>
           <Card sx={{ 
             borderRadius: 3, 
             boxShadow: 3,
             border: '1px solid #e0e0e0',
             transform: 'translate3d(0,0,0)',
             transition: 'transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease',
             '&:hover': {
               transform: 'translate3d(0,-6px,0)',
               boxShadow: '0 18px 44px rgba(0,0,0,0.16)',
               borderColor: 'rgba(25,118,210,0.25)',
             },
             '@media (prefers-reduced-motion: reduce)': {
               transition: 'none',
               '&:hover': { transform: 'none' },
             },
           }}>
             <CardContent sx={{ p: 4 }}>
               <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: 'primary.main', fontSize: '1.1rem' }}>
                 Cómo llegar
               </Typography>
               <Box sx={{ 
                 display: 'flex', 
                 flexDirection: { xs: 'column', md: 'row' }, 
                 gap: 4,
                 justifyContent: 'space-between'
               }}>
                 {routes.map((route, idx) => (
                   <Box key={idx} sx={{ flex: 1, maxWidth: 300 }}>
                     <Reveal
                       delay={120 + idx * 90}
                       variant={idx % 3 === 0 ? 'fade-up' : idx % 3 === 1 ? 'slide-right' : 'slide-left'}
                       distance={20}
                     >
                       <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                         
                         <Box>
                           <Typography variant="h6" sx={{ 
                             fontWeight: 700, 
                             mb: 1, 
                             color: 'text.primary',
                             fontSize: '1rem'
                           }}>
                             {route.title}
                           </Typography>
                           <Typography variant="subtitle2" sx={{ 
                             fontWeight: 600, 
                             color: 'primary.main',
                             fontSize: '0.9rem',
                             mb: 1
                           }}>
                             {route.distance}
                           </Typography>
                           <Typography sx={{ 
                             color: 'text.secondary', 
                             lineHeight: 1.5,
                             fontSize: '0.9rem'
                           }}>
                             {route.description}
                           </Typography>
                         </Box>
                       </Box>
                     </Reveal>
                   </Box>
                 ))}
               </Box>
             </CardContent>
           </Card>
         </Box>
        </Stagger>
      </Box>
    </Box>
  );
}